import React, { useState, useEffect } from 'react';
import detailBtn from '@/assets/images/路径 1794@2x.png';
import styles from './Step1.module.scss';

import { Cascader, Upload, message, Modal } from 'antd';
import { getProductCategoryAllList } from '@/api/products';
import { useAppSelector } from '@/store/hooks';
import type { UploadProps, UploadFile } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// 级联选择器选项类型
interface CascaderOption {
  value: number;
  label: string;
  children?: CascaderOption[];
  isLeaf?: boolean;
}

// 分类类型定义
interface IClassification {
  is_active: boolean;
  is_delete: boolean;
  order: any;
  parent_id: number;
  product_category_id: number;
  product_category_name: string;
  child?: IClassification[];
}

// 组 2624@2x.png
interface Step1Props {
  onNext: (data: any) => void;
  defaultValue?: any;
}





// 将原始分类数据转换为Cascader需要的格式
const transformToCascaderOptions = (data: IClassification[] = []): CascaderOption[] => {
  return data.map(item => {
    const hasChildren = item.child && item.child.length > 0;
    return {
      value: item.product_category_id,
      label: item.product_category_name,
      children: hasChildren ? transformToChildrenOptions(item.child) : undefined,
      isLeaf: !hasChildren // 如果没有子分类，则标记为叶子节点
    };
  });
};

// 转换子分类数据
const transformToChildrenOptions = (children: IClassification[] = []): CascaderOption[] => {
  return children.map(child => {
    const hasChildren = child.child && child.child.length > 0;
    return {
      value: child.product_category_id,
      label: child.product_category_name,
      children: hasChildren ? transformToChildrenOptions(child.child) : undefined,
      isLeaf: !hasChildren // 如果没有子分类，则标记为叶子节点
    };
  });
};

// 显示分类路径
const displayRender = (labels: string[]): string => {
  return labels.join(' > ');
};

const Step1: React.FC<Step1Props> = ({ onNext, defaultValue }) => {
  const { t, i18n } = useTranslation();
  const [title, setTitle] = useState(defaultValue?.title || '');
  const [desc, setDesc] = useState(defaultValue?.desc || '');
  const [customAttrs, setCustomAttrs] = useState<{ key: string; value: string }[]>(defaultValue?.customAttrs || [{ key: '', value: '' }]);
  const [errors, setErrors] = useState<{ category?: string; mainImages?: string; title?: string; desc?: string }>({});
  
  // 级联选项
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);

  // 级联选择器选中值
  const [selectedCascaderValue, setSelectedCascaderValue] = useState<number[] | null>(null);

  // 获取token
  const token = useAppSelector((state) => state.user.token);

  // 扩展 UploadFile 类型，添加需要的属性
  interface ExtendedUploadFile extends UploadFile {
    file_id?: number;
    product_media_id?: number;
  }
  
  // 图片上传相关状态
  const [fileList, setFileList] = useState<ExtendedUploadFile[]>(defaultValue?.mainImages || []);

  // 图片上传配置
  const ImageProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: `${token}`,
    },
    listType: 'picture-card',
    maxCount: 5, // 只允许上传五个
    showUploadList: true, // 显示fileList
    fileList: fileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t("Step1.Imagemustsmaller"));
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      // 复制一份文件列表
      const newFileList = [...info.fileList];

      // 对文件状态进行处理
              newFileList.forEach(file => {
          if (file.response) {
            // 从响应中获取文件URL和file_id
            file.url = file?.response?.data?.url;
            (file as ExtendedUploadFile).file_id = file?.response?.data?.file_id; // 获取上传成功后返回的file_id
          }
        });

      // 更新状态
      setFileList(newFileList);
    },
    async onPreview(file: UploadFile) {
      // 预览逻辑
      let src = file.url as string;
      if (!src) {
        src = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as File);
          reader.onload = () => resolve(reader.result as string);
        });
      }

    }
  };

  // 处理分类选择
  const handleCascaderChange = (value: any, selectedOptions: any): void => {
    if (selectedOptions && selectedOptions.length > 0) {
      const lastOption = selectedOptions[selectedOptions.length - 1];
      // 只有当选择的是叶子节点时才设置值
      if (lastOption.isLeaf) {
        // 保存完整路径
        setSelectedCascaderValue(value);
      }
    }
  };

  const handleAttrChange = (idx: number, field: 'key' | 'value', val: string) => {
    setCustomAttrs(attrs => attrs.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };
  const handleAddAttr = () => setCustomAttrs(attrs => [...attrs, { key: '', value: '' }]);
  const handleRemoveAttr = (idx: number) => setCustomAttrs(attrs => attrs.filter((_, i) => i !== idx));



  useEffect(() => {
    getProductCategoryAllList(2).then((res:any) => {
      if (res?.code === 200) {
        const data = res.data || [];
        const options = transformToCascaderOptions(data);
        setCascaderOptions(options);
        
        // 如果有默认值，设置分类选择器
        if (defaultValue?.category) {
          // 如果defaultValue.category是单个ID，需要转换为路径数组
          if (Array.isArray(defaultValue.category)) {
            setSelectedCascaderValue(defaultValue.category);
          } else {
            // 如果是单个ID，需要查找完整路径
            const findPathById = (options: CascaderOption[], targetId: number, currentPath: number[] = []): number[] | null => {
              for (const option of options) {
                const newPath = [...currentPath, option.value];
                if (option.value === targetId) {
                  return newPath;
                }
                if (option.children && option.children.length > 0) {
                  const result = findPathById(option.children, targetId, newPath);
                  if (result) {
                    return result;
                  }
                }
              }
              return null;
            };
            const path = findPathById(options, defaultValue.category);
            if (path) {
              setSelectedCascaderValue(path);
            }
          }
        }
      }
    });
  }, [defaultValue?.category]);

  // 监听 defaultValue 变化，初始化表单数据
  useEffect(() => {
    if (defaultValue) {
      if (defaultValue.title) setTitle(defaultValue.title);
      if (defaultValue.desc) setDesc(defaultValue.desc);
      if (defaultValue.customAttrs) setCustomAttrs(defaultValue.customAttrs);
      if (defaultValue.mainImages) {
        // 确保图片数据格式正确，包含 Ant Design Upload 组件需要的属性
        const formattedImages = defaultValue.mainImages.map((img: any, index: number) => ({
          uid: img.uid || img.product_media_id?.toString() || `image-${index}`,
          name: img.name || `image_${index}.jpg`,
          status: 'done' as const,
          url: img.url || img.product_media_file_url,
          product_id: img.product_id,
          product_media_type: img.product_media_type || 'images',
          product_media_video_cover_id: img.product_media_video_cover_id,
          file_id: img.file_id,
          product_media_id: img.product_media_id
        }));
        setFileList(formattedImages);
      }
      if (defaultValue.category) {
        // 如果defaultValue.category是单个ID，需要转换为路径数组
        if (Array.isArray(defaultValue.category)) {
          setSelectedCascaderValue(defaultValue.category);
        } else {
          // 如果是单个ID，需要查找完整路径
          const findPathById = (options: CascaderOption[], targetId: number, currentPath: number[] = []): number[] | null => {
            for (const option of options) {
              const newPath = [...currentPath, option.value];
              if (option.value === targetId) {
                return newPath;
              }
              if (option.children && option.children.length > 0) {
                const result = findPathById(option.children, targetId, newPath);
                if (result) {
                  return result;
                }
              }
            }
            return null;
          };
          const path = findPathById(cascaderOptions, defaultValue.category);
          if (path) {
            setSelectedCascaderValue(path);
          }
        }
      }
    }
  }, [defaultValue]);

  const validate = () => {
    const newErrors: { category?: string; mainImages?: string; title?: string; desc?: string } = {};
    if (!selectedCascaderValue) {
      newErrors.category = 'Please select a category';
    }
    if (fileList.length < 1) {
      newErrors.mainImages = 'The Main Image should upload at least one image';
    }
    if (!title || !/^[^\s].{0,28}[^\s]$|^[^\s]$/.test(title.trim())) {
      newErrors.title = 'The Service Title cannot be empty and cannot be all Spaces';
    }
    if (!desc || !/^[^\s].{0,28}[^\s]$|^[^\s]$/.test(desc.trim())) {
      newErrors.desc = 'The Service Description cannot be empty and cannot be all Spaces';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validate()) return;
    
    const submitData = { 
      product_id: defaultValue?.product_id || '',
      category: selectedCascaderValue ? selectedCascaderValue[selectedCascaderValue.length - 1] : null, // 只传递最后一级分类ID
      mainImages: fileList.map(file => ({
        uid: file.uid,
        name: file.name,
        status: file.status,
        url: file.url || '',
        product_id: null,
        product_media_type: "images",
        product_media_file_url: file.url || '',
        product_media_video_cover_id: null,
        file_id: file.file_id || null,
        product_media_id: file.product_media_id || null
      })), 
      title, 
      desc, 
      customAttrs 
    };
    onNext(submitData);
  };

  return (
    <div className={styles.stepContainer}>
      <h2 className={styles.stepTitle}>{t("Step1.BasicInformation")}</h2>
      <div className={styles.stepRow}>
        <label className={styles.stepLabel}>
          <span className={styles.requiredStar}>*</span>{t("Step1.ServiceCategory")}
        </label>
        <div className={styles.stepInputBox}>
          <Cascader
            value={selectedCascaderValue || undefined}
            options={cascaderOptions}
            expandTrigger="hover"
            displayRender={displayRender}
            onChange={handleCascaderChange}
            placeholder="Select a category"
            changeOnSelect={false}
            disabled={!!defaultValue?.product_id} 
            // 只允许选择叶子节点
            fieldNames={{ label: 'label', value: 'value', children: 'children' }}
            showSearch={{
              filter: (inputValue, path) =>
                path.some(option =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                ),
              matchInputWidth: true,
              limit: 10, // 限制最多展示多少个匹配项（可选）
            }}
          />
          {errors.category && <div className={styles.errorTip}>{errors.category}</div>}
        </div>
      </div>
      <div className={styles.stepRowTop}>
        <label className={styles.stepLabelTop}>
          <span className={styles.requiredStar}>*</span>{t("Step1.MainImage")}
        </label>
        <div className={styles.stepImageBox}>
          <div className={styles.stepImageTip}>{t("Step1.ImageRequirements")}</div>
          <Upload {...ImageProps}>
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>{t("Step1.Upload")}</div>
            </div>
          </Upload>
          {errors.mainImages && <div className={styles.errorTip}>{errors.mainImages}</div>}
        </div>
      </div>
      <div className={styles.stepRow}>
        <label className={styles.stepLabel}>
          <span className={styles.requiredStar}>*</span>{t("Step1.ServiceTitle")}
        </label>
        <div className={styles.stepInputBox}>
          <div className={styles.inputWrapper}>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={30} className={styles.stepInput} placeholder={t("Step1.NoMore")} />
            <div className={styles.inputCount}>{title.length}/30</div>
          </div>
          {errors.title && <div className={styles.errorTip}>{errors.title}</div>}
        </div>
      </div>
      <div className={styles.stepRow}>
        <label className={styles.stepLabel}>
          <span className={styles.requiredStar}>*</span>{t("Step1.ServiceDescription")}
        </label>
        <div className={styles.stepInputBox}>
          <div className={styles.stepImageTip}>{t("Step1.DescriptionFeatures")}</div>
          <div className={styles.inputWrapper}>
            <input value={desc} onChange={e => setDesc(e.target.value)} maxLength={30} className={styles.stepInput} placeholder={t("Step1.NoMore")} />
            <div className={styles.inputCount}>{desc.length}/30</div>
          </div>
          {errors.desc && <div className={styles.errorTip}>{errors.desc}</div>}
        </div>
      </div>
      <div className={styles.stepRowTop}>
        <label className={styles.stepLabelCustom}>{t("Step1.CustomAttributes")}</label>
        <div className={styles.stepInputBox}>
          <div className={styles.stepImageTip}>{t("Step1.Craftsmanship")}</div>
          {customAttrs.map((attr, idx) => (
            <div key={idx} className={styles.stepCustomRow}>
              <input value={attr.key} onChange={e => handleAttrChange(idx, 'key', e.target.value)} className={styles.stepInputShort} placeholder="Attribute Name" />
              <span className={styles.colon}>:</span>
              <input value={attr.value} onChange={e => handleAttrChange(idx, 'value', e.target.value)} className={styles.stepInputLong} placeholder="Attribute Value" />
              <img src={detailBtn} onClick={() => handleRemoveAttr(idx)} alt='' className={styles.stepCustomBtn} />
            </div>
          ))}
          <button type="button" onClick={handleAddAttr} className={styles.stepAddBtn}>{t("Step1.Add")}</button>
        </div>
      </div>
      <div className={styles.stepBtnRow}>
        <button type="button" onClick={handleNext} className={styles.stepNextBtn}>{t("Step1.Next")}</button>
      </div>


    </div>
  );
};

export default Step1;
